import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { Sheet } from '../components/Sheet'
import {
  MoneyField, DateField, SelectField, TextField, TextAreaField,
} from '../components/Fields'
import { PhotoField } from '../components/PhotoField'
import { ConfirmDelete } from '../components/ConfirmDelete'
import { useAuth } from '../auth/useAuth'
import { canDelete } from '../lib/types'
import { depositIsSettled, depositDeleteBlock } from '../lib/rules'
import { formatMoney } from '../lib/format'
import type { Deposit, DepositKind, DepositStatus, Holder } from '../lib/types'

type HolderKey =
  | 'holder_proprietaire' | 'holder_gerant' | 'holder_mere'
  | 'holder_banque' | 'holder_autre'

const HOLDERS: Holder[] = ['gerant', 'proprietaire', 'mere', 'banque', 'autre']
const KINDS: DepositKind[] = ['caution', 'mois_avance', 'frais_agence']
const STATUSES: DepositStatus[] = ['detenu', 'introuvable', 'rembourse', 'applique']

interface LeaseOption {
  id: string
  tenant: { full_name: string } | null
  unit: { name: string } | null
}

/**
 * Enregistrement et modification d'une caution, d'un mois d'avance ou
 * de frais d'agence.
 *
 * UNE CAUTION SOLDEE EST VERROUILLEE. Remboursee ou appliquee, elle ne
 * se modifie ni ne se supprime plus : l'argent est sorti, et cette
 * sortie a exige un approbateur, une piece et l'etat des lieux de
 * sortie. Changer le montant apres coup contredirait ces pieces. Le
 * declencheur trg_deposit_settled de db/012 le refuse aussi cote base ;
 * l'ecran le dit avant d'essayer.
 *
 * Le passage de « detenue » a « remboursee » reste possible : c'est
 * l'ancien statut qui verrouille, pas le nouveau.
 */
export function DepositSheet({
  leases,
  deposit = null,
  movementCount = 0,
  onClose,
  onSaved,
  onDeleted,
}: {
  leases: LeaseOption[]
  /** Caution existante a modifier. Absente : nouvelle caution. */
  deposit?: Deposit | null
  /** Mouvements d'entree ou de sortie portes par cette caution. */
  movementCount?: number
  onClose: () => void
  onSaved: () => void
  onDeleted?: () => void
}) {
  const { t } = useI18n()
  const { profile } = useAuth()
  const peutSupprimer = canDelete(profile?.role ?? null)

  // Les deux verrous viennent de lib/rules, pas d'ici : ils y sont
  // ecrits une seule fois et testes, et ils disent exactement ce que
  // refusent les declencheurs de db/012.
  const soldee = deposit !== null && depositIsSettled(deposit.status)
  const blocage =
    deposit === null ? null : depositDeleteBlock(deposit.status, movementCount)

  const [leaseId, setLeaseId] = useState(deposit?.lease_id ?? leases[0]?.id ?? '')
  const [kind, setKind] = useState<DepositKind>(deposit?.kind ?? 'caution')
  const [amount, setAmount] = useState<number | null>(deposit?.amount ?? null)
  const [receivedOn, setReceivedOn] = useState(deposit?.received_on ?? '')
  const [heldBy, setHeldBy] = useState<Holder>(deposit?.held_by ?? 'gerant')
  const [heldByNote, setHeldByNote] = useState(deposit?.held_by_note ?? '')
  const [status, setStatus] = useState<DepositStatus>(deposit?.status ?? 'detenu')
  const [proofDoc, setProofDoc] = useState<string | null>(
    deposit?.proof_document ?? null,
  )
  const [notes, setNotes] = useState(deposit?.notes ?? '')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const valide = !soldee && amount !== null && amount >= 0 && leaseId !== ''

  async function submit() {
    if (!valide) return
    setError(null)
    setSubmitting(true)

    const payload = {
      lease_id: leaseId,
      kind,
      amount,
      received_on: receivedOn || null,
      held_by: heldBy,
      held_by_note: heldBy === 'autre' ? heldByNote.trim() || null : null,
      proof_document: proofDoc,
      status,
      notes: notes.trim() || null,
    }

    const { error: e } = deposit
      ? await supabase.from('deposits').update(payload).eq('id', deposit.id)
      : await supabase.from('deposits').insert(payload)

    setSubmitting(false)
    if (e) {
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved()
  }

  return (
    <Sheet
      title={deposit ? t('deposit_edit') : t('deposit_add')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('save')}
      submitting={submitting}
      disabled={!valide}
      error={error}
    >
      {soldee && (
        <div className="banner banner--warn">
          <strong>{t('deposit_locked_settled')}</strong>
        </div>
      )}

      {/* Des mouvements existent : le montant de la caution et celui
          des mouvements doivent rester d'accord. On avertit sans
          interdire, la caution etant encore detenue. */}
      {!soldee && movementCount > 0 && (
        <div className="banner banner--warn">
          <span>{t('deposit_movements_warning', { n: movementCount })}</span>
        </div>
      )}

      <SelectField
        label={t('lease_tenant')}
        required
        value={leaseId}
        onChange={setLeaseId}
        options={leases.map((l) => ({
          value: l.id,
          label: `${l.tenant?.full_name ?? ''} — ${l.unit?.name ?? ''}`,
        }))}
      />

      <SelectField<DepositKind>
        label={t('deposit_kind')}
        required
        value={kind}
        onChange={setKind}
        options={KINDS.map((k) => ({
          value: k,
          label: t(`deposit_kind_${k}` as
            'deposit_kind_caution' | 'deposit_kind_mois_avance'
            | 'deposit_kind_frais_agence'),
        }))}
      />

      <MoneyField
        label={t('deposit_amount')}
        required
        value={amount}
        onChange={setAmount}
      />

      <DateField
        label={t('deposit_received_on')}
        value={receivedOn}
        onChange={setReceivedOn}
      />

      {/* Qui detient physiquement l'argent : la question a laquelle vos
          documents ne repondaient pas. */}
      <SelectField<Holder>
        label={t('deposit_held_by')}
        required
        value={heldBy}
        onChange={setHeldBy}
        options={HOLDERS.map((h) => ({
          value: h,
          label: t(`holder_${h}` as HolderKey),
        }))}
      />

      {heldBy === 'autre' && (
        <TextField
          label={t('deposit_held_by_note')}
          value={heldByNote}
          onChange={setHeldByNote}
        />
      )}

      <SelectField<DepositStatus>
        label={t('deposit_status')}
        required
        value={status}
        onChange={setStatus}
        options={STATUSES.map((s) => ({
          value: s,
          label: t(`deposit_status_${s}` as
            'deposit_status_detenu' | 'deposit_status_rembourse'
            | 'deposit_status_applique' | 'deposit_status_introuvable'),
        }))}
      />

      {/* Le pas est a sens unique : c'est l'ancien statut qui
          verrouille, donc enregistrer « soldee » ferme la porte. On
          l'annonce ici, au moment du choix, et pas seulement apres. */}
      {!soldee && depositIsSettled(status) && (
        <div className="banner banner--warn">
          <strong>{t('deposit_will_lock')}</strong>
        </div>
      )}

      <PhotoField
        label={t('deposit_proof')}
        target={{ kind: 'recu' }}
        documentId={proofDoc}
        onUploaded={setProofDoc}
      />

      <TextAreaField
        label={t('deposit_held_by_note')}
        hint={t('optional')}
        value={notes}
        onChange={setNotes}
      />

      {/* Suppression, reservee au proprietaire par la RLS. Deux
          verrous s'y ajoutent, et chacun a sa phrase : une caution
          soldee ne se supprime pas, et une caution qui porte des
          mouvements ne se supprime pas non plus, ces mouvements etant
          la trace de l'argent. */}
      {deposit && peutSupprimer && (
        <ConfirmDelete
          label={t('deposit_delete')}
          question={t('deposit_delete_confirm', {
            amount: formatMoney(deposit.amount),
          })}
          disabled={submitting}
          blockedReason={
            blocage === 'soldee'
              ? t('deposit_locked_settled')
              : blocage === 'mouvements'
                ? t('deposit_delete_blocked_movements', { n: movementCount })
                : null
          }
          onDelete={async () => {
            const { error: e } = await supabase
              .from('deposits')
              .delete()
              .eq('id', deposit.id)
            if (e) throw new Error(e.message)
            onDeleted?.()
          }}
        />
      )}
    </Sheet>
  )
}
